/**
 * Recovers from Next.js 13's dynamic-route chunk bug: segment names like [slug]
 * get double URL-encoded (%255Bslug%255D) in the client chunk manifest, so SPA
 * navigation/prefetch 404s the chunk and throws a ChunkLoadError. A full reload
 * re-fetches the correct server-rendered page and manifest.
 *
 * Returns true if it triggered a reload, false if it suppressed one — callers
 * that render "nothing while we reload" (see app/error.tsx) must show a fallback
 * UI when this returns false, or the user is left on a blank page.
 *
 * Loop guard is time-windowed per path: if we already reloaded this path within
 * the last RELOAD_WINDOW_MS, the reload didn't fix it (genuinely broken chunk),
 * so we stop. A legitimate chunk error later — e.g. a new deploy invalidated
 * chunks minutes after the first — is past the window and reloads again.
 * Fixed in Next 14 — remove this when upgrading.
 */
const RELOAD_WINDOW_MS = 10_000;

export function reloadOnChunkError(): boolean {
  if (typeof window === 'undefined') return false;
  const key = `chunk-reload:${window.location.pathname}`;
  try {
    const last = Number(sessionStorage.getItem(key));
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked webview). Without a
    // persistent guard a reload could loop, so bail and let the caller show its
    // fallback UI instead of reloading blindly.
    return false;
  }
  window.location.reload();
  return true;
}
